import { baseApi, storefrontPath } from './baseApi'
import { toDateList } from './bookingApi'
import type { BookingSummary, Slot } from '../types/storefront'
import type { CustomerProfile } from '../features/auth/authSlice'

export type BookingFilter = 'upcoming' | 'past'

export interface BookingPage {
  items: BookingSummary[]
  page: number
  lastPage: number
  total: number
}

export interface ProfileDraft {
  first_name: string
  last_name: string
  email: string | null
  gender: string | null
  date_of_birth: string | null
}

interface RawPaginator {
  items?: unknown
  data?: unknown
  meta?: { current_page?: number; last_page?: number; total?: number }
}

/** The account endpoints answer `{items, meta}`, but a bare array is accepted too
 *  so a backend that switches to an unpaginated collection does not break the list. */
function toBookingPage(raw: unknown, requestedPage: number): BookingPage {
  if (Array.isArray(raw)) {
    const items = raw as BookingSummary[]
    return { items, page: 1, lastPage: 1, total: items.length }
  }

  const paginator = (raw ?? {}) as RawPaginator
  const list = Array.isArray(paginator.items) ? paginator.items : paginator.data
  const items = Array.isArray(list) ? (list as BookingSummary[]) : []
  const meta = paginator.meta ?? {}

  return {
    items,
    page: meta.current_page ?? requestedPage,
    lastPage: meta.last_page ?? 1,
    total: meta.total ?? items.length,
  }
}

export const accountApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMyBookings: build.query<BookingPage, { slug: string; status: BookingFilter; page?: number }>({
      query: ({ slug, status, page }) => {
        const params = new URLSearchParams({ status })
        if (page && page > 1) params.set('page', String(page))
        return storefrontPath(slug, `/me/bookings?${params.toString()}`)
      },
      transformResponse: (raw: unknown, _meta, arg) => toBookingPage(raw, arg.page ?? 1),
      providesTags: ['Booking'],
    }),

    getMyBooking: build.query<BookingSummary, { slug: string; id: number }>({
      query: ({ slug, id }) => storefrontPath(slug, `/me/bookings/${id}`),
      providesTags: ['Booking'],
    }),

    cancelMyBooking: build.mutation<BookingSummary, { slug: string; id: number; reason?: string }>({
      query: ({ slug, id, reason }) => ({
        url: storefrontPath(slug, `/me/bookings/${id}/cancel`),
        method: 'POST',
        body: { reason: reason ?? null },
      }),
      // A cancelled slot is free again, so availability is stale too.
      invalidatesTags: ['Booking', 'Availability'],
    }),

    /** The slot grid for moving a booking: its own services and staff, its own slot freed. */
    getRescheduleAvailability: build.query<Slot[], { slug: string; id: number; date: string }>({
      query: ({ slug, id, date }) =>
        storefrontPath(slug, `/me/bookings/${id}/availability?${new URLSearchParams({ date }).toString()}`),
      providesTags: ['Availability'],
    }),

    getRescheduleDates: build.query<string[], { slug: string; id: number; month: string }>({
      query: ({ slug, id, month }) =>
        storefrontPath(slug, `/me/bookings/${id}/availability/dates?${new URLSearchParams({ month }).toString()}`),
      transformResponse: toDateList,
      providesTags: ['Availability'],
    }),

    rescheduleMyBooking: build.mutation<BookingSummary, { slug: string; id: number; startsAt: string }>({
      query: ({ slug, id, startsAt }) => ({
        url: storefrontPath(slug, `/me/bookings/${id}/reschedule`),
        method: 'POST',
        body: { starts_at: startsAt },
      }),
      // The old slot is free again and the new one taken.
      invalidatesTags: ['Booking', 'Availability'],
    }),

    getProfile: build.query<CustomerProfile, string>({
      query: (slug) => storefrontPath(slug, '/me/profile'),
      providesTags: ['Profile'],
    }),

    updateProfile: build.mutation<CustomerProfile, { slug: string } & ProfileDraft>({
      query: ({ slug, ...body }) => ({
        url: storefrontPath(slug, '/me/profile'),
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Profile'],
    }),
  }),
})

export const {
  useGetMyBookingsQuery,
  useGetMyBookingQuery,
  useCancelMyBookingMutation,
  useGetRescheduleAvailabilityQuery,
  useGetRescheduleDatesQuery,
  useRescheduleMyBookingMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
} = accountApi
